import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useLanguage } from '@/contexts/LanguageContext';
import { useUserSession } from '@/hooks/useUserSession';

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setLanguage } = useLanguage();
  const { saveUserSession } = useUserSession();
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    language: "en"
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Update language context
    setLanguage(formData.language as 'en' | 'pt' | 'es');
    
    // Get position ID from URL if present
    const positionId = searchParams.get('position');
    
    // Save to ISOLATED assessment storage (cannot be overwritten by AuthContext)
    // This is critical for logged-in users taking assessments with different emails
    localStorage.setItem('assessmentFormData', JSON.stringify({
      fullName: formData.fullName,
      email: formData.email,
      language: formData.language,
      timestamp: Date.now(),
      position_id: positionId || undefined
    }));
    
    // Also save to regular session (for backwards compatibility)
    saveUserSession(formData);
    
    // Preserve validation and position parameters
    const validationParam = searchParams.get('validation');
    let targetPath = '/assessment';
    const params = new URLSearchParams();
    if (validationParam) params.set('validation', validationParam);
    if (positionId) params.set('position', positionId);
    if (params.toString()) targetPath += `?${params.toString()}`;
    
    navigate(targetPath);
  };

  const isFormValid = formData.fullName.trim() !== "" && 
                     formData.email.trim() !== "" && 
                     formData.email.includes('@');

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-sm">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-6">
            <div className="w-8 h-8 bg-foreground rounded flex items-center justify-center">
              <span className="text-background font-bold text-sm">Z</span>
            </div>
            <span className="text-lg font-medium text-foreground">Zentrix Insights</span>
          </div>
          <h1 className="text-xl font-semibold text-foreground mb-2">
            Start your assessment
          </h1>
          <p className="text-sm text-muted-foreground">
            Takes about 10 minutes
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="fullName" className="block text-sm text-muted-foreground mb-1.5">
              Name
            </label>
            <Input
              id="fullName"
              type="text"
              placeholder="Your full name"
              value={formData.fullName}
              onChange={(e) => setFormData(prev => ({ ...prev, fullName: e.target.value }))}
              required
              className="h-10 bg-background border-border"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm text-muted-foreground mb-1.5">
              Email
            </label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              required
              className="h-10 bg-background border-border"
            />
          </div>

          <div>
            <label htmlFor="language" className="block text-sm text-muted-foreground mb-1.5">
              Language
            </label>
            <Select 
              value={formData.language} 
              onValueChange={(value) => setFormData(prev => ({ ...prev, language: value }))}
            >
              <SelectTrigger id="language" className="h-10 bg-background border-border">
                <SelectValue placeholder="Select language" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="en">English</SelectItem>
                <SelectItem value="es">Español</SelectItem>
                <SelectItem value="pt">Português</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button 
            type="submit" 
            className="w-full h-10 bg-foreground text-background hover:bg-foreground/90 mt-6"
            disabled={!isFormValid}
          >
            Continue
          </Button>
        </form>

        {/* Footer */}
        <div className="mt-6 text-center">
          <button 
            type="button"
            onClick={() => navigate('/')}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            ← Back to home
          </button>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
